import { FocusEvent, FormEvent, useCallback, useRef, useState } from "react";
import { useIntl } from "react-intl";
import { useLiveQuery } from "dexie-react-hooks";
import { toast } from "react-toastify";

import { useForm } from "../../../hooks/form-hook";
import { InputsType } from "../../../hooks/form-hook-typing";
import { useGoogleGeocoding } from "../../../hooks/useGoogleGeocoding";

import Loading from "../../Loading";
import Modal from "../../UIelements/Modal";
import InputForwardRef, { InputForwardType } from "../../FormElements/InputForwardRef";
import PrimaryButton from "../../FormElements/PrimaryButton";

import { ViaCepDataType } from "../../../models/ViaCep";
import { UpdateLocationRequestType } from "../../../models/Location";
import { db } from "../../../db/db";
import { getZipCodeInfo, updateLocation } from "../../../services";

import { VALIDATOR_MAXLENGTH, VALIDATOR_REQUIRE } from "../../../utils/validators";

import * as Styled from './styles'

type ModalProps = {
  onCancel: () => void;
  onUpdateList: () => void;
  location: any;
};

enum InputLocationNames {
  NAME = 'locationName',
  ZIP_CODE = 'zipCode',
  CITY = 'city',
  STATE = 'state',
  NEIGHBORHOOD = 'neighborhood',
  COUNTRY = 'country',
  STREET = 'street',
  NUMBER = 'number',
};

const initialValue = {
  value: '',
  isValid: false,
};

const initialLocationInputs: InputsType = {
  [InputLocationNames.NAME]: initialValue,
  [InputLocationNames.ZIP_CODE]: initialValue,
  [InputLocationNames.CITY]: initialValue,
  [InputLocationNames.STATE]: initialValue,
  [InputLocationNames.NEIGHBORHOOD]: initialValue,
  [InputLocationNames.COUNTRY]: initialValue,
  [InputLocationNames.STREET]: initialValue,
  [InputLocationNames.NUMBER]: initialValue,
};

type LocationType = { lat: number; lng: number };

function formatZipCode(zipCode: any) {
  let zipCodeFormat = String(zipCode).replace('-', '')
  return +zipCodeFormat;
}

const getSubmitLocationData = (
  inputs: InputsType,
  location: LocationType,
  id: number,
  companyId: number,
): UpdateLocationRequestType => ({
  location: {
    id: id,
    locationName: inputs[InputLocationNames.NAME].value,
    zipCode: formatZipCode(inputs[InputLocationNames.ZIP_CODE].value),
    street: inputs[InputLocationNames.STREET].value,
    number: +inputs[InputLocationNames.NUMBER].value,
    neighborhood: inputs[InputLocationNames.NEIGHBORHOOD].value,
    country: inputs[InputLocationNames.COUNTRY].value,
    state: inputs[InputLocationNames.STATE].value,
    city: inputs[InputLocationNames.CITY].value,
    latitude: location.lat,
    longitude: location.lng,
    companyId: companyId,
    showLocation: true
  },
});

const ModalUpdateLocation = (props: ModalProps) => {
  const [isLoading, setIsLoading] = useState(false);
  const intl = useIntl();
  const { getCoordinates, isLoaded, loadError, isLoading: isLoadingGeocoding } = useGoogleGeocoding();
  const [zipCodeFound, setZipCodeFound] = useState(false);
  const userDb = useLiveQuery(() => db.user.toArray())
  const [formLocationState, inputLocationHandler] = useForm(
    initialLocationInputs,
    false
  );

  const locationNameRef = useRef<InputForwardType>(null);
  const zipCodeRef = useRef<InputForwardType>(null);
  const cityRef = useRef<InputForwardType>(null);
  const stateRef = useRef<InputForwardType>(null);
  const neighborhoodRef = useRef<InputForwardType>(null);
  const countryRef = useRef<InputForwardType>(null);
  const streetRef = useRef<InputForwardType>(null);
  const numberRef = useRef<InputForwardType>(null);

  const handleUpdateLocation = useCallback(
    async (location: LocationType): Promise<boolean> => {
      const data = getSubmitLocationData(
        formLocationState.inputs,
        location,
        props.location.id,
        userDb![0].companyId
      );
      try {
        await updateLocation(data, userDb![0].accessToken)
        toast.success(
          intl.formatMessage({ id: 'equipment.Localização editada com sucesso!' }).toUpperCase()
        );
        return true;
      } catch (error) {
        toast.error(
          intl.formatMessage({
            id: 'equipment.Não foi possível editar a localização. Tente novamente mais tarde',
          }).toUpperCase()
        );
        return false;
      }
    },
    [userDb, formLocationState.inputs, intl, props]

  );

  const handleGetAddress = async (event: FocusEvent<HTMLInputElement>) => {
    try {
      const zipCode = event.target.value;
      await getZipCodeInfo(zipCode)
        .then(response => {
          if (response.data.erro === 'true') {
            setZipCodeFound(false)
          } else {
            setZipCodeFound(true)
            const data: ViaCepDataType = response.data;
            if (cityRef && cityRef.current)
              cityRef.current.changeHandler(data.localidade);
            if (stateRef && stateRef.current) stateRef.current.changeHandler(data.uf);
            if (neighborhoodRef && neighborhoodRef.current)
              neighborhoodRef.current.changeHandler(data.bairro);
            if (streetRef && streetRef.current)
              streetRef.current.changeHandler(data.logradouro);
          }
        })
    } catch (error) {
    }
  };

  const handleGetLocation = useCallback(async (): Promise<LocationType> => {
    if (!isLoaded || loadError) {
      console.warn("Geocoding indisponível: API não carregada ou bloqueada.");
      return { lat: 0, lng: 0 };
    }

    const address =
      formLocationState.inputs[InputLocationNames.STREET].value +
      ' ' +
      formLocationState.inputs[InputLocationNames.NUMBER].value +
      ' ' +
      formLocationState.inputs[InputLocationNames.CITY].value +
      ' ' +
      formLocationState.inputs[InputLocationNames.STATE].value;

    try {
      const results = await getCoordinates(address.trim());

      const geometry = results[0].geometry.location;

      return {
        lat: geometry.lat(),
        lng: geometry.lng()
      };
    } catch (error) {
      console.error('Erro ao buscar coordenadas na edição:', error);
      return { lat: 0, lng: 0 };
    }
  }, [formLocationState.inputs, isLoaded, loadError, getCoordinates]);

  const handleSubmit = useCallback(
    async (event: FormEvent) => {
      setIsLoading(true);
      event.preventDefault();
      if (!formLocationState.isValid) {
        setIsLoading(false);
        return;
      }
      const location = await handleGetLocation();
      const isSuccess = await handleUpdateLocation(location);
      setIsLoading(false);
      if (!isSuccess) return;
      props.onUpdateList();
      props.onCancel();
    },
    [
      formLocationState.isValid,
      handleUpdateLocation,
      props
    ]
  );

  const loading = <Loading />;

  const footer = (
    <>
      <PrimaryButton
        type='submit'
        size='buttonModal'
        model='confirmation-gradient-modal'
        width='108px'
      >
        {intl.formatMessage({ id: "store.salvar" })}
      </PrimaryButton>
      <PrimaryButton
        type='button'
        size='buttonModal'
        model='cancellation'
        width='108px'
        onClick={props.onCancel}
      >
        {intl.formatMessage({ id: "menus.CANCELAR" })}
      </PrimaryButton>
    </>
  );

  function formatCep(cep: string): string {
    const cleanedCep = cep.replace(/\D/g, '');

    if (cleanedCep.length !== 8) {
      return ''
    }

    const formattedCep = `${cleanedCep.slice(0, 5)}-${cleanedCep.slice(5)}`;
    return formattedCep;
  }

  const content = (
    <>
      <Styled.Container>
        <Styled.Content flexGrow={1}>
          <Styled.Row>
            <InputForwardRef
              id={InputLocationNames.NAME}
              ref={locationNameRef}
              type='text'
              label={intl.formatMessage({ id: 'equipment.Nome do espaço' }) + ' *'}
              initialValid={true}
              initialValue={props.location.locationName}
              validators={[VALIDATOR_REQUIRE(), VALIDATOR_MAXLENGTH(22)]}
              maxLength={22}
              placeholder={intl.formatMessage({
                id: 'equipment.Informe o nome do espaço',
              })}
              errorText={intl.formatMessage({
                id: 'equipment.Preencha o nome do espaço',
              })}
              onInput={inputLocationHandler}
            />
          </Styled.Row>
          <Styled.Row>
            <InputForwardRef
              id={InputLocationNames.ZIP_CODE}
              ref={zipCodeRef}
              type='text'
              label={intl.formatMessage({ id: 'equipment.CEP' }) + ' *'}
              initialValid={true}
              validators={[VALIDATOR_REQUIRE()]}
              initialValue={formatCep(String(props.location.zipCode))}
              placeholder={intl.formatMessage({
                id: 'equipment.00000-000',
              })}
              errorText={intl.formatMessage({
                id: 'equipment.preencha o CEP',
              })}
              onInput={inputLocationHandler}
              onBlur={handleGetAddress}
              maskType='ZIP_CODE'
            />
          </Styled.Row>
          <Styled.Row>
            <InputForwardRef
              id={InputLocationNames.CITY}
              ref={cityRef}
              type='text'
              label={intl.formatMessage({ id: 'equipment.cidade' }) + ' *'}
              initialValue={props.location.city}
              initialValid={!formLocationState.inputs[InputLocationNames.ZIP_CODE].value}
              error2={!!formLocationState.inputs[InputLocationNames.ZIP_CODE].value && !formLocationState.inputs[InputLocationNames.CITY].value}
              validators={[VALIDATOR_REQUIRE()]}
              placeholder={intl.formatMessage({
                id: 'equipment.informe a cidade',
              })}
              errorText={intl.formatMessage({
                id: 'equipment.preencha a cidade',
              })}
              errorText2={intl.formatMessage({
                id: 'equipment.preencha a cidade',
              })}
              onInput={inputLocationHandler}
            />
          </Styled.Row>
          <Styled.Row>
            <InputForwardRef
              id={InputLocationNames.STATE}
              ref={stateRef}
              type='text'
              label={intl.formatMessage({ id: 'equipment.UF' }) + ' *'}
              initialValue={props.location.state}
              initialValid={!formLocationState.inputs[InputLocationNames.ZIP_CODE].value}
              error2={!!formLocationState.inputs[InputLocationNames.ZIP_CODE].value && !formLocationState.inputs[InputLocationNames.STATE].value}
              validators={[VALIDATOR_REQUIRE()]}
              placeholder={intl.formatMessage({
                id: 'equipment.UF',
              })}
              errorText=''
              onInput={inputLocationHandler}
            />
          </Styled.Row>
          <Styled.Row>
            <InputForwardRef
              id={InputLocationNames.NEIGHBORHOOD}
              ref={neighborhoodRef}
              type='text'
              label={intl.formatMessage({ id: 'equipment.bairro' }) + ' *'}
              initialValid={true}
              validators={[VALIDATOR_REQUIRE()]}
              initialValue={props.location.neighborhood}
              placeholder={intl.formatMessage({
                id: 'equipment.informe o bairro',
              })}
              errorText={intl.formatMessage({
                id: 'equipment.preencha o bairro',
              })}
              onInput={inputLocationHandler}
            />
          </Styled.Row>
          <Styled.Row>
            <InputForwardRef
              id={InputLocationNames.COUNTRY}
              ref={countryRef}
              type='text'
              label={intl.formatMessage({ id: 'equipment.país' }) + ' *'}
              initialValue={props.location.country}
              initialValid={true}
              validators={[VALIDATOR_REQUIRE()]}
              placeholder={intl.formatMessage({
                id: 'equipment.informe o país',
              })}
              errorText={intl.formatMessage({
                id: 'equipment.preencha o país',
              })}
              onInput={inputLocationHandler}
            />
          </Styled.Row>
          <Styled.Row>
            <InputForwardRef
              id={InputLocationNames.STREET}
              ref={streetRef}
              type='text'
              label={intl.formatMessage({ id: 'equipment.Endereço (rua, avenida, rodovia)' }) + ' *'}
              initialValid={true}
              validators={[VALIDATOR_REQUIRE()]}
              initialValue={props.location.street}
              placeholder={intl.formatMessage({
                id: 'equipment.informe o seu endereço',
              })}
              errorText={intl.formatMessage({
                id: 'equipment.preencha o seu endereço',
              })}
              onInput={inputLocationHandler}
            />
          </Styled.Row>
          <Styled.Row>
            <InputForwardRef
              id={InputLocationNames.NUMBER}
              ref={numberRef}
              type='number'
              label={intl.formatMessage({ id: 'equipment.Nº/Km' }) + ' *'}
              initialValid={true}
              validators={[VALIDATOR_REQUIRE()]}
              initialValue={String(props.location.number)}
              placeholder={intl.formatMessage({
                id: 'equipment.Nº/Km',
              })}
              onInput={inputLocationHandler}
            />
          </Styled.Row>
        </Styled.Content>
      </Styled.Container>
    </>
  );

  return (
    <>
      <Modal
        title={intl.formatMessage({ id: "equipment.Editar localização" })}
        width="650px"
        children={isLoading ? loading : content}
        footer={footer}
        onCancel={props.onCancel}
        onSubmit={handleSubmit}
      />
    </>
  );
}

export default ModalUpdateLocation;